// The receipt OCR request, with nothing build-time left in it (#112).
//
// `receiptOcr.js` reads `import.meta.env` for its service URL. Vite
// substitutes that at build time; Babel emits it verbatim, so under jest that
// file fails to parse — lazily reading it does not help (#106) — and anything
// defined beside it can never be covered by the pure suite. So the request and
// the sentences it can hand a teacher live here, and `receiptOcr.js` holds
// only the build-time configuration.
//
// This module imports `./fileLimits` and the core and nothing else — no Vite,
// no Firebase, no emulator — so `test/domain/receiptOcrClient.test.js` runs in
// the pure suite. The base URL and the signed-in user arrive as arguments
// rather than as imports, so no dependency hides behind a `moduleNameMapper`
// entry or a Babel rewrite.

import { scanTooLarge } from './fileLimits';
import { coreSync } from '../domain/core.js';

// The sentences are the core's (#472, `service_message` in
// core/src/files.rs), which both phones read too.
const said = (code, status, error) => coreSync().serviceMessage('receipt', code, status ?? null, error ?? null);

export class ReceiptOcrError extends Error {}

// `?places=0&dateOrder=ymd`: how the service is to read this studio's
// receipts (services/receipt-ocr/README.md). Empty when neither is known.
export function readingQuery(currency, datePattern) {
  const q = new URLSearchParams();
  if (currency) q.set('places', String(coreSync().exponentFor(String(currency))));
  if (datePattern) q.set('dateOrder', coreSync().dateOrder(String(datePattern)));
  const s = q.toString();
  return s ? `?${s}` : '';
}

// A trailing slash on the configured URL would produce `//v1/receipts:extract`.
// Exported because the caller needs the same answer to decide whether the
// service is configured at all: a value of `"/"` is a configured-looking string
// that normalises to nothing.
export const normaliseBaseUrl = (value) => String(value || '').replace(/\/+$/, '');

// `getUser` is a function rather than a user because `auth.currentUser` is read
// at call time, not at wiring time — a scan started before the auth state
// settles would otherwise capture a null forever.
//
// `fetchImpl` defaults to the ambient `fetch` looked up per call, so a test can
// hand in its own without the module having captured the global at load.
export function makeExtractReceipt({ baseUrl, getUser, fetchImpl } = {}) {
  const BASE = normaliseBaseUrl(baseUrl);
  const doFetch = fetchImpl || ((...args) => fetch(...args));

  // `currency` and `datePattern` are the studio's, which is how the receipt's
  // figures and dates are read (#501): the query says how many decimal places
  // the currency has and which order a numeric date is in, each the core's
  // answer (`money::exponent_for`, `locale::date_order`), and the reply's
  // `amountMinor` is in minor units of that currency. Omitted, the service
  // reads two places and month-first. `decimal` is the Region's decimal
  // separator, for the size in the too-large sentence (#583); omitted, a point.
  return async function extractReceipt(file, {
    signal, currency, datePattern, decimal = '.',
  } = {}) {
    if (!BASE) throw new ReceiptOcrError(said('not_set_up'));
    if (!file) throw new ReceiptOcrError(said('no_file'));
    // The reader's cap, not the store's (12 MB against 2 GB, #96). See
    // scanTooLarge.
    const tooBig = scanTooLarge(file, decimal);
    if (tooBig) throw new ReceiptOcrError(tooBig);

    // The service identifies the caller by their Firebase ID token — the app
    // holds no secret of its own, because anything in a browser bundle is
    // public.
    const user = getUser?.();
    if (!user) throw new ReceiptOcrError(said('signed_out'));
    const token = await user.getIdToken();

    let res;
    try {
      res = await doFetch(`${BASE}/v1/receipts:extract${readingQuery(currency, datePattern)}`, {
        method: 'POST',
        signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      // A CORS rejection and a service that is down are indistinguishable from
      // here, so the message names both rather than guessing.
      throw new ReceiptOcrError(said('unreachable'));
    }

    let body = null;
    try { body = await res.json(); } catch { /* handled below */ }

    if (!res.ok) {
      throw new ReceiptOcrError(said('refused', res.status, typeof body?.error === 'string' ? body.error : null));
    }
    if (!body) throw new ReceiptOcrError(said('unreadable'));
    return body;
  };
}
